/**
 * Product image placeholder — CSS-only, category-aware.
 *
 * Used by Shop, Cart, Checkout, and any other product surface when the
 * product has no real photo (or its seeded image URL is on the denylist).
 *
 * Renders a gradient panel with a category-relevant lucide icon plus the
 * category label. No external image fetches — impossible to display the
 * wrong subject by accident.
 */
import {
  Package, Anchor, Cog, Settings, Box, Shirt, Briefcase, Cookie, Fish,
} from 'lucide-react';

const CATEGORY_META = {
  Rods:        { icon: Anchor,    label: 'Rod',         gradient: 'from-[#1a2b1f] to-[#0b0d0b]' },
  Reels:       { icon: Cog,       label: 'Reel',        gradient: 'from-[#2a2014] to-[#0b0d0b]' },
  Tackle:      { icon: Settings,  label: 'Tackle',      gradient: 'from-[#1a1f2b] to-[#0b0d0b]' },
  Bait:        { icon: Cookie,    label: 'Bait',        gradient: 'from-[#2b2014] to-[#0b0d0b]' },
  Clothing:    { icon: Shirt,     label: 'Clothing',    gradient: 'from-[#1a1a2b] to-[#0b0d0b]' },
  Accessories: { icon: Box,       label: 'Accessories', gradient: 'from-[#2b1a1f] to-[#0b0d0b]' },
  Luggage:     { icon: Briefcase, label: 'Luggage',     gradient: 'from-[#1f1a2b] to-[#0b0d0b]' },
  Bivvy:       { icon: Box,       label: 'Bivvy',       gradient: 'from-[#1a2b2b] to-[#0b0d0b]' },
  Bedchair:    { icon: Box,       label: 'Bedchair',    gradient: 'from-[#2b261a] to-[#0b0d0b]' },
  Nets:        { icon: Fish,      label: 'Net',         gradient: 'from-[#142b2b] to-[#0b0d0b]' },
};

const DEFAULT_META = { icon: Package, label: 'Gear', gradient: 'from-[#1f221f] to-[#0b0d0b]' };

const ProductImagePlaceholder = ({ category, compact = false, testId }) => {
  const meta = CATEGORY_META[category] || DEFAULT_META;
  const Icon = meta.icon;
  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br ${meta.gradient} border border-[#1f221f]`}
      data-testid={testId || 'product-image-placeholder'}
    >
      <Icon className={`${compact ? 'w-6 h-6' : 'w-12 h-12'} text-[#c8b273]/70`} strokeWidth={1.4} />
      {!compact && (
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#c8b273]/80 font-bold">{meta.label}</p>
      )}
    </div>
  );
};

export default ProductImagePlaceholder;
